import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import { adminAPI } from '../api/admin.api';
import toast from 'react-hot-toast';
import {
  Box,
  Container,
  Paper,
  Typography,
  TextField,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Avatar,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  Toolbar,
  Tooltip,
  Card,
  CardContent,
  Divider,
  Stack,
  CircularProgress,
  InputAdornment,
  AppBar,
  Tabs,
  Tab,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Alert,
} from '@mui/material';
import {
  Search,
  Visibility,
  GetApp,
  FilterList,
  CheckCircle,
  Cancel,
  ChevronLeft,
  ChevronRight,
  Image,
  Person,
  Email,
  Phone,
  LocationOn,
  CreditCard,
  CalendarToday,
  Close,
  Download,
  Edit,
  MedicalServices,
  Delete,
  ThumbUp,
  ThumbDown,
  Receipt,
  Badge,
  History,
  ExpandMore,
  TrendingUp,
  EmojiEvents,
  Home,
  Bloodtype,
  School,
  AccountBalance,
  Description,
  Info,
  Warning as WarningIcon,
  FamilyRestroom,
} from '@mui/icons-material';

// TabPanel component for tabs
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`athlete-tabpanel-${index}`}
      aria-labelledby={`athlete-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const AthletesList = () => {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, logout } = useAuth();
  const [athletes, setAthletes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [selectedAthlete, setSelectedAthlete] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showImageModal, setShowImageModal] = useState(false);
  const [currentImage, setCurrentImage] = useState(null);
  const [selectedAthletes, setSelectedAthletes] = useState([]);
  const [showVerificationModal, setShowVerificationModal] = useState(false);
  const [verificationNotes, setVerificationNotes] = useState('');
  const [medicalAge, setMedicalAge] = useState('');
  const [updatingMedicalAge, setUpdatingMedicalAge] = useState(false);
  const [editingAthlete, setEditingAthlete] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteAthleteId, setDeleteAthleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [pagination, setPagination] = useState({});
  const [searchInput, setSearchInput] = useState('');
  const [downloadingAdmitCards, setDownloadingAdmitCards] = useState(false);
  const [events, setEvents] = useState([]);
  const [downloadingInvoices, setDownloadingInvoices] = useState(false);
  const [tabValue, setTabValue] = useState(0); // For tabs in modal

  const [states] = useState([
    'All States',
    'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
    'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand', 'Karnataka',
    'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur', 'Meghalaya', 'Mizoram',
    'Nagaland', 'Odisha', 'Punjab', 'Rajasthan', 'Sikkim', 'Tamil Nadu',
    'Telangana', 'Tripura', 'Uttar Pradesh', 'Uttarakhand', 'West Bengal',
    'Andaman and Nicobar Islands', 'Chandigarh', 'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi', 'Jammu and Kashmir', 'Ladakh', 'Lakshadweep', 'Puducherry'
  ]);

  const [filters, setFilters] = useState({
    registrationType: 'all',
    verificationStatus: 'all',
    paymentStatus: 'completed',
    state: 'all',
    eventId: 'all',
    startDate: '',
    endDate: '',
    search: '',
    page: 1,
    limit: 20,
    sortBy: 'createdAt',
    sortOrder: 'desc',
  });

  useEffect(() => {
    const fetchEvents = async () => {
      try {
        const response = await fetch('https://register.iwlf.in/api/events', {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('token')}`,
          },
        });
        const data = await response.json();
        if (data.success) {
          setEvents(data.data);
        }
      } catch (error) {
        console.error('Error fetching events:', error);
      }
    };
    fetchEvents();
  }, []);

  useEffect(() => {
    if (!isAuthenticated || !isAdmin) {
      navigate('/admin/login');
      return;
    }
    fetchAthletes();
  }, [isAuthenticated, isAdmin, navigate, filters]);

  const fetchAthletes = async () => {
    setLoading(true);
    try {
      const response = await adminAPI.getAllAthletes(filters);
      setAthletes(response.data);
      setPagination(response.pagination);
    } catch (error) {
      toast.error('Failed to load athletes');
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setFilters({ ...filters, search: searchInput, page: 1 });
  };

  const handleFilterChange = (key, value) => {
    if (key === 'page' || key === 'limit') {
      setFilters({ ...filters, [key]: value });
    } else {
      setFilters({ ...filters, [key]: value, page: 1 });
    }
  };

  const handleViewDetails = async (athleteId) => {
    try {
      const response = await adminAPI.getAthleteDetails(athleteId);
      setSelectedAthlete(response.data);
      setMedicalAge(response.data.medicalAge || '');
      setTabValue(0); // Reset to first tab
      setShowModal(true);
    } catch (error) {
      toast.error('Failed to load athlete details');
    }
  };

  const handleQuickApprove = async (athleteId) => {
    try {
      await adminAPI.verifyAthlete(athleteId, {
        status: 'approved',
        notes: 'Quick approval from table',
      });
      toast.success('Athlete approved successfully');
      fetchAthletes();
    } catch (error) {
      console.error('Approve error:', error);
      toast.error(error.response?.data?.message || 'Approval failed');
    }
  };

  const handleQuickReject = async (athleteId) => {
    try {
      await adminAPI.verifyAthlete(athleteId, {
        status: 'rejected',
        notes: 'Quick rejection from table',
      });
      toast.success('Athlete rejected successfully');
      fetchAthletes();
    } catch (error) {
      console.error('Reject error:', error);
      toast.error(error.response?.data?.message || 'Rejection failed');
    }
  };

  const handleDeleteAthlete = async () => {
    if (!deleteAthleteId) return;
    setDeleting(true);
    try {
      const response = await adminAPI.deleteAthlete(deleteAthleteId);
      if (response.data?.warning) {
        toast.warning(response.data.warning, { duration: 5000 });
      } else {
        toast.success('Athlete deleted successfully');
      }
      setShowDeleteConfirm(false);
      setDeleteAthleteId(null);
      fetchAthletes();
    } catch (error) {
      console.error('Delete error:', error);
      toast.error(error.response?.data?.message || 'Failed to delete athlete');
    } finally {
      setDeleting(false);
    }
  };

  const handleBulkDelete = async () => {
    if (selectedAthletes.length === 0) {
      toast.error('Please select athletes to delete');
      return;
    }
    const confirmed = window.confirm(
      `Are you sure you want to delete ${selectedAthletes.length} athlete(s)? This action cannot be undone.`
    );
    if (!confirmed) return;
    try {
      const response = await adminAPI.bulkDeleteAthletes(selectedAthletes);
      if (response.data?.warning) {
        toast.warning(response.data.warning, { duration: 5000 });
      }
      toast.success(response.message);
      setSelectedAthletes([]);
      fetchAthletes();
    } catch (error) {
      console.error('Bulk delete error:', error);
      toast.error(error.response?.data?.message || 'Bulk delete failed');
    }
  };

  const handleVerifyAthlete = async (athleteId, status) => {
    try {
      await adminAPI.verifyAthlete(athleteId, {
        status,
        notes: verificationNotes,
      });
      toast.success(`Athlete ${status} successfully`);
      setShowModal(false);
      setVerificationNotes('');
      fetchAthletes();
    } catch (error) {
      console.error('Verification error:', error);
      toast.error(error.response?.data?.message || 'Verification failed');
    }
  };

  const handleBulkVerify = async (status) => {
    if (selectedAthletes.length === 0) {
      toast.error('Please select athletes to verify');
      return;
    }
    try {
      await adminAPI.bulkVerifyAthletes({
        athleteIds: selectedAthletes,
        status,
        notes: verificationNotes,
      });
      toast.success(`${selectedAthletes.length} athletes ${status} successfully`);
      setSelectedAthletes([]);
      setShowVerificationModal(false);
      setVerificationNotes('');
      fetchAthletes();
    } catch (error) {
      console.error('Bulk verification error:', error);
      toast.error('Bulk verification failed');
    }
  };

  const handleUpdateMedicalAge = async (athleteId) => {
    if (!medicalAge || medicalAge < 0 || medicalAge > 150) {
      toast.error('Please enter a valid medical age (0-150)');
      return;
    }
    setUpdatingMedicalAge(true);
    try {
      await adminAPI.updateMedicalAge(athleteId, {
        medicalAge: Number(medicalAge),
      });
      toast.success('Medical age updated successfully');
      const response = await adminAPI.getAthleteDetails(athleteId);
      setSelectedAthlete(response.data);
    } catch (error) {
      console.error('Medical age update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update medical age');
    } finally {
      setUpdatingMedicalAge(false);
    }
  };

  const handleExportCSV = async () => {
    setExporting(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.registrationType !== 'all') queryParams.append('registrationType', filters.registrationType);
      if (filters.verificationStatus !== 'all') queryParams.append('verificationStatus', filters.verificationStatus);
      if (filters.paymentStatus !== 'all') queryParams.append('paymentStatus', filters.paymentStatus);
      if (filters.state !== 'all') queryParams.append('state', filters.state);
      if (filters.eventId !== 'all') queryParams.append('eventId', filters.eventId);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);

      const url = `https://register.iwlf.in/api/admin/athletes/export${
        queryParams.toString() ? `?${queryParams}` : ''
      }`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Export failed');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `athletes-export-${Date.now()}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      toast.success('Data exported successfully');
    } catch (error) {
      console.error('Export error:', error);
      toast.error(error.message || 'Failed to export data');
    } finally {
      setExporting(false);
    }
  };

  const handleDownloadInvoices = async () => {
    setDownloadingInvoices(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.registrationType !== 'all') queryParams.append('registrationType', filters.registrationType);
      if (filters.verificationStatus !== 'all') queryParams.append('verificationStatus', filters.verificationStatus);
      if (filters.paymentStatus !== 'all') queryParams.append('paymentStatus', filters.paymentStatus);
      if (filters.state !== 'all') queryParams.append('state', filters.state);
      if (filters.eventId !== 'all') queryParams.append('eventId', filters.eventId);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);

      const url = `https://register.iwlf.in/api/admin/athletes/invoices/download?${queryParams}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download invoices');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `invoices-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      toast.success('Invoices downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download invoices');
    } finally {
      setDownloadingInvoices(false);
    }
  };

  const handleDownloadAdmitCards = async () => {
    setDownloadingAdmitCards(true);
    try {
      const queryParams = new URLSearchParams();
      if (filters.registrationType !== 'all') queryParams.append('registrationType', filters.registrationType);
      if (filters.eventId !== 'all') queryParams.append('eventId', filters.eventId);
      if (filters.state !== 'all') queryParams.append('state', filters.state);
      if (filters.startDate) queryParams.append('startDate', filters.startDate);
      if (filters.endDate) queryParams.append('endDate', filters.endDate);
      if (filters.search) queryParams.append('search', filters.search);

      const url = `https://register.iwlf.in/api/admin/athletes/admit-cards/download?${queryParams}`;
      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to download admit cards');
      }

      const blob = await response.blob();
      const downloadUrl = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = downloadUrl;
      a.download = `admit-cards-${Date.now()}.zip`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(downloadUrl);
      document.body.removeChild(a);
      toast.success('Admit cards downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error(error.message || 'Failed to download admit cards');
    } finally {
      setDownloadingAdmitCards(false);
    }
  };

  const handleDownloadSingleInvoice = async (athlete) => {
    if (!athlete.invoicePath) {
      toast.error('Invoice not available for this athlete');
      return;
    }
    try {
      window.open(athlete.invoicePath, '_blank');
      toast.success('Opening invoice...');
    } catch (error) {
      toast.error('Failed to download invoice');
    }
  };

  const handleDownloadSingleAdmitCard = async (athlete) => {
    if (!athlete.admitCardPath || !athlete.admitCardGenerated) {
      toast.error('Admit card not available for this athlete');
      return;
    }
    try {
      window.open(athlete.admitCardPath, '_blank');
      toast.success('Opening admit card...');
    } catch (error) {
      toast.error('Failed to download admit card');
    }
  };

  const handleImageView = (imageUrl, title) => {
    setCurrentImage({ url: imageUrl, title });
    setShowImageModal(true);
  };

  const handleImageDownload = async (imageUrl, filename) => {
    try {
      const response = await fetch(imageUrl);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('File downloaded successfully');
    } catch (error) {
      console.error('Download error:', error);
      toast.error('Failed to download image');
    }
  };

  const toggleAthleteSelection = (athleteId) => {
    setSelectedAthletes((prev) =>
      prev.includes(athleteId)
        ? prev.filter((id) => id !== athleteId)
        : [...prev, athleteId]
    );
  };

  const toggleSelectAll = () => {
    if (selectedAthletes.length === athletes.length) {
      setSelectedAthletes([]);
    } else {
      setSelectedAthletes(athletes.map((a) => a._id));
    }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'warning',
      approved: 'success',
      rejected: 'error',
      completed: 'success',
      failed: 'error',
    };
    return colors[status] || 'default';
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('en-IN', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
    });
  };

  const formatAmount = (amount) => {
    if (typeof amount === 'number') {
      return (amount / 100).toFixed(2);
    }
    return amount || '0.00';
  };

  const handleEditAthlete = () => {
    setEditingAthlete(true);
    setEditFormData({
      firstName: selectedAthlete.firstName || '',
      middleName: selectedAthlete.middleName || '',
      surname: selectedAthlete.surname || '',
      email: selectedAthlete.email || '',
      phoneNumber: selectedAthlete.phoneNumber || '',
      bloodGroup: selectedAthlete.bloodGroup || '',
      district: selectedAthlete.district || '',
      stateDepartment: selectedAthlete.stateDepartment || '',
      addressLine1: selectedAthlete.addressLine1 || '',
      addressLine2: selectedAthlete.addressLine2 || '',
      city: selectedAthlete.city || '',
      state: selectedAthlete.state || '',
      pincode: selectedAthlete.pincode || '',
    });
  };

  const handleSaveEdit = async () => {
    if (!editFormData.firstName || !editFormData.surname) {
      toast.error('First name and surname are required');
      return;
    }
    if (!editFormData.email) {
      toast.error('Email is required');
      return;
    }
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(editFormData.email)) {
      toast.error('Invalid email format');
      return;
    }
    if (editFormData.phoneNumber) {
      const phoneRegex = /^[6-9]\d{9}$/;
      if (!phoneRegex.test(editFormData.phoneNumber)) {
        toast.error('Invalid phone number format');
        return;
      }
    }
    try {
      setUpdatingMedicalAge(true);
      await adminAPI.updateAthleteDetails(selectedAthlete._id, editFormData);
      toast.success('Athlete details updated successfully');
      setEditingAthlete(false);
      const response = await adminAPI.getAthleteDetails(selectedAthlete._id);
      setSelectedAthlete(response.data);
      fetchAthletes();
    } catch (error) {
      console.error('Update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update athlete details');
    } finally {
      setUpdatingMedicalAge(false);
    }
  };

  const handleCancelEdit = () => {
    setEditingAthlete(false);
    setEditFormData({});
  };

  const normalizeUrl = (url) => {
    if (!url) return null;
    if (url.startsWith('https://') || url.startsWith('http://')) return url;
    return `https://register.iwlf.in${url}`;
  };

  if (loading && athletes.length === 0) {
    return (
      <Container maxWidth="xl" sx={{ mt: 4 }}>
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
          <CircularProgress size={60} />
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
      {/* Header */}
      <Box sx={{ mb: 3 }}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
          Athletes List
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage and verify athlete registrations
        </Typography>
      </Box>

      {/* Search Bar */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <TextField
          fullWidth
          placeholder="Search by name, email, phone, invoice number, admit card number..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
          onKeyPress={(e) => e.key === 'Enter' && handleSearch(e)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search />
              </InputAdornment>
            ),
            endAdornment: searchInput && (
              <InputAdornment position="end">
                <IconButton
                  onClick={() => {
                    setSearchInput('');
                    setFilters({ ...filters, search: '', page: 1 });
                  }}
                >
                  <Close />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
      </Paper>

      {/* Filter Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <FilterList sx={{ mr: 1 }} />
          <Typography variant="h6">Filters</Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Registration Type */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Type</InputLabel>
              <Select
                value={filters.registrationType}
                label="Type"
                onChange={(e) => handleFilterChange('registrationType', e.target.value)}
              >
                <MenuItem value="all">All Types</MenuItem>
                <MenuItem value="youth">Youth</MenuItem>
                <MenuItem value="junior">Junior</MenuItem>
                <MenuItem value="senior">Senior</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Verification Status */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Verification</InputLabel>
              <Select
                value={filters.verificationStatus}
                label="Verification"
                onChange={(e) => handleFilterChange('verificationStatus', e.target.value)}
              >
                <MenuItem value="all">All Status</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="approved">Approved</MenuItem>
                <MenuItem value="rejected">Rejected</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* Payment Status */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Payment</InputLabel>
              <Select
                value={filters.paymentStatus}
                label="Payment"
                onChange={(e) => handleFilterChange('paymentStatus', e.target.value)}
              >
                <MenuItem value="all">All Payments</MenuItem>
                <MenuItem value="completed">Completed</MenuItem>
                <MenuItem value="pending">Pending</MenuItem>
                <MenuItem value="failed">Failed</MenuItem>
              </Select>
            </FormControl>
          </Grid>

          {/* State */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>State</InputLabel>
              <Select
                value={filters.state}
                label="State"
                onChange={(e) => handleFilterChange('state', e.target.value)}
              >
                <MenuItem value="all">All States</MenuItem>
                {states.slice(1).map((state) => (
                  <MenuItem key={state} value={state}>
                    {state}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Event */}
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Event</InputLabel>
              <Select
                value={filters.eventId}
                label="Event"
                onChange={(e) => handleFilterChange('eventId', e.target.value)}
              >
                <MenuItem value="all">All Events</MenuItem>
                {events.map((event) => (
                  <MenuItem key={event._id} value={event._id}>
                    {event.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          {/* Start Date */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Start Date"
              type="date"
              size="small"
              value={filters.startDate}
              onChange={(e) => handleFilterChange('startDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* End Date */}
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="End Date"
              type="date"
              size="small"
              value={filters.endDate}
              onChange={(e) => handleFilterChange('endDate', e.target.value)}
              InputLabelProps={{
                shrink: true,
              }}
            />
          </Grid>

          {/* Clear Filters Button */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="outlined"
              onClick={() => {
                setFilters({
                  registrationType: 'all',
                  verificationStatus: 'all',
                  paymentStatus: 'completed',
                  state: 'all',
                  eventId: 'all',
                  startDate: '',
                  endDate: '',
                  search: '',
                  page: 1,
                  limit: 20,
                  sortBy: 'createdAt',
                  sortOrder: 'desc',
                });
                setSearchInput('');
              }}
              sx={{ height: '40px' }}
            >
              Clear Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Action Buttons Section */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Box display="flex" alignItems="center" mb={2}>
          <GetApp sx={{ mr: 1 }} />
          <Typography variant="h6">Actions</Typography>
        </Box>

        <Grid container spacing={2}>
          {/* Export CSV */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<GetApp />}
              onClick={handleExportCSV}
              disabled={exporting}
              sx={{ height: '44px' }}
            >
              {exporting ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Exporting...
                </>
              ) : (
                'Export CSV'
              )}
            </Button>
          </Grid>

          {/* Download Invoices */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Receipt />}
              onClick={handleDownloadInvoices}
              disabled={downloadingInvoices}
              sx={{ height: '44px' }}
            >
              {downloadingInvoices ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Downloading...
                </>
              ) : (
                'Bulk Invoices'
              )}
            </Button>
          </Grid>

          {/* Download Admit Cards */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              startIcon={<Badge />}
              onClick={handleDownloadAdmitCards}
              disabled={downloadingAdmitCards}
              sx={{ height: '44px' }}
            >
              {downloadingAdmitCards ? (
                <>
                  <CircularProgress size={20} color="inherit" sx={{ mr: 1 }} />
                  Downloading...
                </>
              ) : (
                'Bulk Admit Cards'
              )}
            </Button>
          </Grid>

          {/* Search/Apply Filters Button */}
          <Grid item xs={12} sm={6} md={3}>
            <Button
              fullWidth
              variant="contained"
              color="primary"
              startIcon={<Search />}
              onClick={handleSearch}
              sx={{ height: '44px' }}
            >
              Apply Filters
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {/* Results Summary */}
      {pagination.totalRecords !== undefined && (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Showing {athletes.length} of {pagination.totalRecords} athlete(s)
          {filters.search && ` matching "${filters.search}"`}
        </Typography>
      )}

      {/* Bulk Actions */}
      {selectedAthletes.length > 0 && (
        <Paper sx={{ p: 2, mb: 2, bgcolor: 'action.selected' }}>
          <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
            <Typography variant="body1" fontWeight="bold">
              {selectedAthletes.length} athlete(s) selected
            </Typography>
            <Button
              variant="contained"
              color="success"
              size="small"
              startIcon={<ThumbUp />}
              onClick={() => setShowVerificationModal(true)}
            >
              Bulk Approve
            </Button>
            <Button
              variant="contained"
              color="error"
              size="small"
              startIcon={<ThumbDown />}
              onClick={() => setShowVerificationModal(true)}
            >
              Bulk Reject
            </Button>
            <Button
              variant="outlined"
              color="error"
              size="small"
              startIcon={<Delete />}
              onClick={handleBulkDelete}
            >
              Delete Selected
            </Button>
            <Button
              variant="outlined"
              size="small"
              onClick={() => setSelectedAthletes([])}
            >
              Clear Selection
            </Button>
          </Stack>
        </Paper>
      )}

      {/* Athletes Table */}
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: 'action.hover' }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={
                    athletes.length > 0 &&
                    selectedAthletes.length === athletes.length
                  }
                  onChange={toggleSelectAll}
                  indeterminate={
                    selectedAthletes.length > 0 &&
                    selectedAthletes.length < athletes.length
                  }
                />
              </TableCell>
              <TableCell>Athlete</TableCell>
              <TableCell>Type</TableCell>
              <TableCell>Contact</TableCell>
              <TableCell>City</TableCell>
              <TableCell>State</TableCell>
              <TableCell>Event</TableCell>
              <TableCell>Payment</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>Medical Age</TableCell>
              <TableCell>Registered</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {athletes.length === 0 ? (
              <TableRow>
                <TableCell colSpan={12} align="center">
                  <Box py={4}>
                    <Typography variant="h6" color="text.secondary">
                      No athletes found
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Try adjusting your filters
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              athletes.map((athlete) => (
                <TableRow key={athlete._id} hover style={{backgroundColor:athlete.upgradeHistory?athlete.upgradeHistory.length>0?'#eac5c5':'white':'white'}}>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedAthletes.includes(athlete._id)}
                      onChange={() => toggleAthleteSelection(athlete._id)}
                    />
                  </TableCell>
                  <TableCell>
                    <Box display="flex" alignItems="center" gap={2}>
                      {athlete.photo?.path ? (
                        <Avatar
                          src={normalizeUrl(athlete.photo.path)}
                          onClick={() =>
                            handleImageView(normalizeUrl(athlete.photo.path), 'Athlete Photo')
                          }
                          sx={{ cursor: 'pointer' }}
                        />
                      ) : (
                        <Avatar>
                          {athlete.firstName?.charAt(0)}
                          {athlete.surname?.charAt(0)}
                        </Avatar>
                      )}
                      <Box>
                        <Typography variant="body2" fontWeight="500">
                          {athlete.firstName} {athlete.middleName}{' '}
                          {athlete.surname}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {athlete.email}
                        </Typography>
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={athlete.registrationType}
                      size="small"
                      color="primary"
                    />
                  </TableCell>
                  <TableCell>
                    <Typography variant="body2">{athlete.phoneNumber}</Typography>
                  </TableCell>
                  <TableCell>{athlete.city || '-'}</TableCell>
                  <TableCell>{athlete.state || '-'}</TableCell>
                  <TableCell>
                    <Typography variant="body2" noWrap>
                      {athlete.selectedEvent?.eventName || '-'}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={athlete.paymentStatus}
                      size="small"
                      color={getStatusColor(athlete.paymentStatus)}
                    />
                    {athlete.amount && (
                      <Typography variant="caption" display="block">
                        ₹{(athlete.amount / 100).toFixed(2)}
                      </Typography>
                    )}
                    {athlete.invoiceNumber && (
                      <Typography variant="caption" display="block" color="text.secondary">
                        {athlete.invoiceNumber}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={athlete.verificationStatus}
                      size="small"
                      color={getStatusColor(athlete.verificationStatus)}
                    />
                  </TableCell>
                  <TableCell>{athlete.medicalAge || '-'}</TableCell>
                  <TableCell>{formatDate(athlete.createdAt)}</TableCell>
                  <TableCell>
                    <Stack direction="row" spacing={1}>
                      <Tooltip title="View Details">
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={() => handleViewDetails(athlete._id)}
                        >
                          <Visibility />
                        </IconButton>
                      </Tooltip>
                      {athlete.invoicePath && (
                        <Tooltip title="Download Invoice">
                          <IconButton
                            size="small"
                            color="info"
                            onClick={() => handleDownloadSingleInvoice(athlete)}
                          >
                            <Receipt />
                          </IconButton>
                        </Tooltip>
                      )}
                      {athlete.admitCardGenerated && athlete.admitCardPath && (
                        <Tooltip title="Download Admit Card">
                          <IconButton
                            size="small"
                            color="secondary"
                            onClick={() => handleDownloadSingleAdmitCard(athlete)}
                          >
                            <Badge />
                          </IconButton>
                        </Tooltip>
                      )}
                      {athlete.verificationStatus === 'pending' && (
                        <>
                          <Tooltip title="Quick Approve">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleQuickApprove(athlete._id)}
                            >
                              <ThumbUp />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Quick Reject">
                            <IconButton
                              size="small"
                              color="error"
                              onClick={() => handleQuickReject(athlete._id)}
                            >
                              <ThumbDown />
                            </IconButton>
                          </Tooltip>
                        </>
                      )}
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => {
                            setDeleteAthleteId(athlete._id);
                            setShowDeleteConfirm(true);
                          }}
                        >
                          <Delete />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>

        {/* Pagination */}
        <TablePagination
          component="div"
          count={pagination.totalRecords || 0}
          page={(filters.page || 1) - 1}
          onPageChange={(e, newPage) =>
            handleFilterChange('page', newPage + 1)
          }
          rowsPerPage={filters.limit}
          onRowsPerPageChange={(e) =>
            handleFilterChange('limit', parseInt(e.target.value))
          }
          rowsPerPageOptions={[10, 20, 50, 100]}
        />
      </TableContainer>

      {/* Athlete Details Modal with Tabs */}
      <Dialog
        open={showModal}
        onClose={() => {
          setShowModal(false);
          setEditingAthlete(false);
          setTabValue(0);
        }}
        maxWidth="lg"
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <Typography variant="h6">Athlete Details</Typography>
            <IconButton onClick={() => setShowModal(false)}>
              <Close />
            </IconButton>
          </Box>
          {selectedAthlete && (
            <Tabs
              value={tabValue}
              onChange={(e, newValue) => setTabValue(newValue)}
              sx={{ borderBottom: 1, borderColor: 'divider', mt: 2 }}
            >
              <Tab label="Personal Info" />
              <Tab label="Documents" />
              <Tab label="Payment & Event" />
              <Tab label="Invoice History" />
              <Tab label="Admit Card" />
              <Tab label="Upgrade History" />
            </Tabs>
          )}
        </DialogTitle>

        <DialogContent dividers>
          {selectedAthlete && (
            <>
              {/* Tab 1: Personal Information */}
              <TabPanel value={tabValue} index={0}>
                <Grid container spacing={3}>
                  {/* Photo */}
                  <Grid item xs={12} textAlign="center">
                    {selectedAthlete.photo?.path ? (
                      <Avatar
                        src={normalizeUrl(selectedAthlete.photo.path)}
                        onClick={() =>
                          handleImageView(normalizeUrl(selectedAthlete.photo.path), 'Athlete Photo')
                        }
                        sx={{ width: 120, height: 120, mx: 'auto', cursor: 'pointer' }}
                      />
                    ) : (
                      <Avatar sx={{ width: 120, height: 120, mx: 'auto', fontSize: 48 }}>
                        {selectedAthlete.firstName?.charAt(0)}
                        {selectedAthlete.surname?.charAt(0)}
                      </Avatar>
                    )}
                  </Grid>

                  {/* Personal Details */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      Personal Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="First Name"
                      value={editingAthlete ? editFormData.firstName : selectedAthlete.firstName}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, firstName: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Middle Name"
                      value={editingAthlete ? editFormData.middleName : selectedAthlete.middleName}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, middleName: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Surname"
                      value={editingAthlete ? editFormData.surname : selectedAthlete.surname}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, surname: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Email"
                      value={editingAthlete ? editFormData.email : selectedAthlete.email}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, email: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      value={editingAthlete ? editFormData.phoneNumber : selectedAthlete.phoneNumber}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, phoneNumber: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Date of Birth"
                      value={formatDate(selectedAthlete.birthDate)}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Gender"
                      value={selectedAthlete.gender}
                      disabled
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Registration Type"
                      value={selectedAthlete.registrationType}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={6}>
                    <TextField
                      fullWidth
                      label="Aadhar Number"
                      value={selectedAthlete.aadharCardNumber}
                      disabled
                      size="small"
                    />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Blood Group"
                      value={editingAthlete ? editFormData.bloodGroup : selectedAthlete.bloodGroup || '-'}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, bloodGroup: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="District"
                      value={editingAthlete ? editFormData.district : selectedAthlete.district || '-'}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, district: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State Department"
                      value={editingAthlete ? editFormData.stateDepartment : selectedAthlete.stateDepartment || '-'}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, stateDepartment: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>

                  {selectedAthlete.kheloIndiaId && (
                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Khelo India ID"
                        value={selectedAthlete.kheloIndiaId}
                        disabled
                        size="small"
                      />
                    </Grid>
                  )}

                  {/* Father's Information */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Father's Information
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Father's First Name"
                      value={selectedAthlete.fatherFirstName}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Father's Middle Name"
                      value={selectedAthlete.fatherMiddleName || '-'}
                      disabled
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Father's Surname"
                      value={selectedAthlete.fatherSurname}
                      disabled
                      size="small"
                    />
                  </Grid>

                  {/* Mother's Information */}
                  {selectedAthlete.motherFirstName && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                          Mother's Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                      </Grid>

                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Mother's First Name"
                          value={selectedAthlete.motherFirstName}
                          disabled
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Mother's Middle Name"
                          value={selectedAthlete.motherMiddleName || '-'}
                          disabled
                          size="small"
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <TextField
                          fullWidth
                          label="Mother's Surname"
                          value={selectedAthlete.motherSurname}
                          disabled
                          size="small"
                        />
                      </Grid>
                    </>
                  )}

                  {/* Address */}
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                      Address
                    </Typography>
                    <Divider sx={{ mb: 2 }} />
                  </Grid>

                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 1"
                      value={editingAthlete ? editFormData.addressLine1 : selectedAthlete.addressLine1}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, addressLine1: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Address Line 2"
                      value={editingAthlete ? editFormData.addressLine2 : selectedAthlete.addressLine2 || '-'}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, addressLine2: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="City"
                      value={editingAthlete ? editFormData.city : selectedAthlete.city}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, city: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="State"
                      value={editingAthlete ? editFormData.state : selectedAthlete.state}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, state: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12} sm={4}>
                    <TextField
                      fullWidth
                      label="Pincode"
                      value={editingAthlete ? editFormData.pincode : selectedAthlete.pincode}
                      onChange={(e) =>
                        setEditFormData({ ...editFormData, pincode: e.target.value })
                      }
                      disabled={!editingAthlete}
                      size="small"
                    />
                  </Grid>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Country"
                      value={selectedAthlete.country}
                      disabled
                      size="small"
                    />
                  </Grid>

                  {/* Medical Age for Junior/Youth */}
                  {['junior', 'youth'].includes(selectedAthlete.registrationType) && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                          Medical Information
                        </Typography>
                        <Divider sx={{ mb: 2 }} />
                      </Grid>

                      <Grid item xs={12} sm={8}>
                        <TextField
                          fullWidth
                          label="Medical Age (Years)"
                          type="number"
                          value={medicalAge}
                          onChange={(e) => setMedicalAge(e.target.value)}
                          placeholder="Enter medical age"
                          size="small"
                          InputProps={{
                            startAdornment: (
                              <InputAdornment position="start">
                                <MedicalServices />
                              </InputAdornment>
                            ),
                          }}
                        />
                      </Grid>
                      <Grid item xs={12} sm={4}>
                        <Button
                          fullWidth
                          variant="contained"
                          onClick={() => handleUpdateMedicalAge(selectedAthlete._id)}
                          disabled={updatingMedicalAge}
                          sx={{ height: '40px' }}
                        >
                          {updatingMedicalAge ? <CircularProgress size={20} /> : 'Update'}
                        </Button>
                      </Grid>

                      {selectedAthlete.medicalAge && (
                        <Grid item xs={12}>
                          <Alert severity="info">
                            Current Medical Age: {selectedAthlete.medicalAge} years
                            {selectedAthlete.medicalAgeFilledAt && (
                              <> (Updated: {formatDate(selectedAthlete.medicalAgeFilledAt)})</>
                            )}
                          </Alert>
                        </Grid>
                      )}
                    </>
                  )}
                </Grid>
              </TabPanel>

              {/* Tab 2: Documents */}
              <TabPanel value={tabValue} index={1}>
                <Grid container spacing={3}>
                  {/* Medical Certificate */}
                  {selectedAthlete.medicalCertificate?.path && (
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Medical Certificate (PDF)
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {selectedAthlete.medicalCertificate.filename}
                          </Typography>
                          <Stack direction="row" spacing={2} mt={2}>
                            <Button
                              variant="contained"
                              startIcon={<Visibility />}
                              onClick={() =>
                                window.open(
                                  normalizeUrl(selectedAthlete.medicalCertificate.path),
                                  '_blank'
                                )
                              }
                            >
                              Open Medical Certificate
                            </Button>
                            <Button
                              variant="outlined"
                              startIcon={<Download />}
                              onClick={() =>
                                handleImageDownload(
                                  normalizeUrl(selectedAthlete.medicalCertificate.path),
                                  selectedAthlete.medicalCertificate.filename || 'medical-certificate.pdf'
                                )
                              }
                            >
                              Download
                            </Button>
                          </Stack>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Identity Proof */}
                  {selectedAthlete.identityProof?.front?.path && (
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Identity Proof - Front
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {selectedAthlete.identityProof.type}
                          </Typography>
                          <img
                            src={normalizeUrl(selectedAthlete.identityProof.front.path)}
                            alt="Identity Proof Front"
                            style={{ width: '100%', maxHeight: 300, objectFit: 'contain', cursor: 'pointer' }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.identityProof.front.path), 'Identity Proof - Front')}
                          />
                          <Button
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 2 }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.identityProof.front.path), 'Identity Proof - Front')}
                          >
                            View Full Size
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {selectedAthlete.identityProof?.back?.path && (
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Identity Proof - Back
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {selectedAthlete.identityProof.type}
                          </Typography>
                          <img
                            src={normalizeUrl(selectedAthlete.identityProof.back.path)}
                            alt="Identity Proof Back"
                            style={{ width: '100%', maxHeight: 300, objectFit: 'contain', cursor: 'pointer' }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.identityProof.back.path), 'Identity Proof - Back')}
                          />
                          <Button
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 2 }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.identityProof.back.path), 'Identity Proof - Back')}
                          >
                            View Full Size
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Proof of Age */}
                  {selectedAthlete.proofOfAge?.front?.path && (
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Proof of Age - Front
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {selectedAthlete.proofOfAge.type}
                          </Typography>
                          <img
                            src={normalizeUrl(selectedAthlete.proofOfAge.front.path)}
                            alt="Proof of Age Front"
                            style={{ width: '100%', maxHeight: 300, objectFit: 'contain', cursor: 'pointer' }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.proofOfAge.front.path), 'Proof of Age - Front')}
                          />
                          <Button
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 2 }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.proofOfAge.front.path), 'Proof of Age - Front')}
                          >
                            View Full Size
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {selectedAthlete.proofOfAge?.back?.path && (
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Proof of Age - Back
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {selectedAthlete.proofOfAge.type}
                          </Typography>
                          <img
                            src={normalizeUrl(selectedAthlete.proofOfAge.back.path)}
                            alt="Proof of Age Back"
                            style={{ width: '100%', maxHeight: 300, objectFit: 'contain', cursor: 'pointer' }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.proofOfAge.back.path), 'Proof of Age - Back')}
                          />
                          <Button
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 2 }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.proofOfAge.back.path), 'Proof of Age - Back')}
                          >
                            View Full Size
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Address Proof */}
                  {selectedAthlete.addressProof?.front?.path && (
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Address Proof - Front
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {selectedAthlete.addressProof.type}
                          </Typography>
                          <img
                            src={normalizeUrl(selectedAthlete.addressProof.front.path)}
                            alt="Address Proof Front"
                            style={{ width: '100%', maxHeight: 300, objectFit: 'contain', cursor: 'pointer' }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.addressProof.front.path), 'Address Proof - Front')}
                          />
                          <Button
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 2 }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.addressProof.front.path), 'Address Proof - Front')}
                          >
                            View Full Size
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {selectedAthlete.addressProof?.back?.path && (
                    <Grid item xs={12} md={6}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Address Proof - Back
                          </Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {selectedAthlete.addressProof.type}
                          </Typography>
                          <img
                            src={normalizeUrl(selectedAthlete.addressProof.back.path)}
                            alt="Address Proof Back"
                            style={{ width: '100%', maxHeight: 300, objectFit: 'contain', cursor: 'pointer' }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.addressProof.back.path), 'Address Proof - Back')}
                          />
                          <Button
                            fullWidth
                            variant="outlined"
                            sx={{ mt: 2 }}
                            onClick={() => handleImageView(normalizeUrl(selectedAthlete.addressProof.back.path), 'Address Proof - Back')}
                          >
                            View Full Size
                          </Button>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}
                </Grid>
              </TabPanel>

              {/* Tab 3: Payment & Event */}
              <TabPanel value={tabValue} index={2}>
                <Grid container spacing={3}>
                  {/* Current Event */}
                  <Grid item xs={12}>
                    <Card sx={{ border: '2px solid', borderColor: 'primary.main', bgcolor: 'action.hover' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="primary" fontWeight="bold">
                          Current Event Registration
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Event Name:
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {selectedAthlete.selectedEvent?.eventName || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Event Amount:
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              ₹{selectedAthlete.selectedEvent?.eventAmount || '0'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Period:
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {selectedAthlete.selectedEvent?.eventPeriod || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Registered On:
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {formatDate(selectedAthlete.selectedEvent?.registeredAt)}
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Latest Payment Details */}
                  <Grid item xs={12}>
                    <Card sx={{ border: '2px solid', borderColor: 'success.main', bgcolor: 'success.lighter' }}>
                      <CardContent>
                        <Typography variant="h6" gutterBottom color="success.main" fontWeight="bold">
                          Latest Payment Details
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Invoice Number:
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {selectedAthlete.invoiceNumber || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Payment ID:
                            </Typography>
                            <Typography variant="body2" fontWeight="500">
                              {selectedAthlete.paymentId || '-'}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Payment Status:
                            </Typography>
                            <Chip
                              label={selectedAthlete.paymentStatus}
                              size="small"
                              color={getStatusColor(selectedAthlete.paymentStatus)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Amount Paid:
                            </Typography>
                            <Typography variant="h6" color="success.main" fontWeight="bold">
                              ₹{formatAmount(selectedAthlete.amount)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Payment Date:
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {formatDate(selectedAthlete.paymentDate)}
                            </Typography>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Order ID:
                            </Typography>
                            <Typography variant="body2" fontWeight="500">
                              {selectedAthlete.orderId || '-'}
                            </Typography>
                          </Grid>
                        </Grid>

                        {selectedAthlete.invoicePath && (
                          <Box mt={3}>
                            <Button
                              fullWidth
                              variant="contained"
                              color="success"
                              startIcon={<Download />}
                              onClick={() => handleDownloadSingleInvoice(selectedAthlete)}
                            >
                              Download Latest Invoice
                            </Button>
                          </Box>
                        )}
                      </CardContent>
                    </Card>
                  </Grid>

                  {/* Payment Breakdown */}
                  {selectedAthlete.paymentBreakdown && (
                    <Grid item xs={12}>
                      <Card>
                        <CardContent>
                          <Typography variant="h6" gutterBottom>
                            Payment Breakdown
                          </Typography>
                          <Divider sx={{ my: 2 }} />
                          <TableContainer>
                            <Table size="small">
                              <TableBody>
                                <TableRow>
                                  <TableCell>Base Amount:</TableCell>
                                  <TableCell align="right">
                                    ₹{formatAmount(selectedAthlete.paymentBreakdown.baseAmount)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>
                                    GST @ {selectedAthlete.paymentBreakdown.gstRate}%:
                                  </TableCell>
                                  <TableCell align="right">
                                    ₹{formatAmount(selectedAthlete.paymentBreakdown.gstAmount)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>
                                    Convenience Fee @ {selectedAthlete.paymentBreakdown.convenienceRate}%:
                                  </TableCell>
                                  <TableCell align="right">
                                    ₹{formatAmount(selectedAthlete.paymentBreakdown.convenienceAmount)}
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell colSpan={2}>
                                    <Divider />
                                  </TableCell>
                                </TableRow>
                                <TableRow>
                                  <TableCell>
                                    <strong>Total Amount:</strong>
                                  </TableCell>
                                  <TableCell align="right">
                                    <strong>₹{formatAmount(selectedAthlete.paymentBreakdown.totalAmount)}</strong>
                                  </TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </CardContent>
                      </Card>
                    </Grid>
                  )}

                  {/* Verification */}
                  <Grid item xs={12}>
                    <Card>
                      <CardContent>
                        <Typography variant="h6" gutterBottom>
                          Verification
                        </Typography>
                        <Divider sx={{ my: 2 }} />
                        <Grid container spacing={2}>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Verification Status:
                            </Typography>
                            <Chip
                              label={selectedAthlete.verificationStatus}
                              size="small"
                              color={getStatusColor(selectedAthlete.verificationStatus)}
                            />
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Typography variant="body2" color="text.secondary">
                              Verification Date:
                            </Typography>
                            <Typography variant="body1" fontWeight="500">
                              {formatDate(selectedAthlete.verificationDate)}
                            </Typography>
                          </Grid>
                          {selectedAthlete.verificationNotes && (
                            <Grid item xs={12}>
                              <Typography variant="body2" color="text.secondary">
                                Verification Notes:
                              </Typography>
                              <Typography variant="body1">
                                {selectedAthlete.verificationNotes}
                              </Typography>
                            </Grid>
                          )}
                          <Grid item xs={12}>
                            <TextField
                              fullWidth
                              multiline
                              rows={3}
                              label="Add/Update Verification Notes"
                              value={verificationNotes}
                              onChange={(e) => setVerificationNotes(e.target.value)}
                              placeholder="Add notes for verification (optional)"
                            />
                          </Grid>
                        </Grid>
                      </CardContent>
                    </Card>
                  </Grid>
                </Grid>
              </TabPanel>

              {/* Tab 4: Invoice History */}
              <TabPanel value={tabValue} index={3}>
                {selectedAthlete.invoices && selectedAthlete.invoices.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell><strong>Invoice #</strong></TableCell>
                          <TableCell><strong>Type</strong></TableCell>
                          <TableCell><strong>Event</strong></TableCell>
                          <TableCell><strong>Amount</strong></TableCell>
                          <TableCell><strong>Date</strong></TableCell>
                          <TableCell align="center"><strong>Action</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedAthlete.invoices.map((invoice, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body2" fontWeight="500">
                                {invoice.invoiceNumber}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Chip
                                label={invoice.invoiceType === 'initial' ? 'Initial' : 'Upgrade'}
                                color={invoice.invoiceType === 'initial' ? 'primary' : 'success'}
                                size="small"
                              />
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {invoice.relatedEvent?.eventName || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="500">
                                ₹{formatAmount(invoice.amount)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(invoice.paymentDate)}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Tooltip title="Download Invoice">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() => window.open(invoice.invoicePath, '_blank')}
                                >
                                  <Download />
                                </IconButton>
                              </Tooltip>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    <Typography variant="body1">No invoice history available</Typography>
                    <Typography variant="body2">
                      {selectedAthlete.invoiceNumber
                        ? 'Only the current invoice is available.'
                        : 'Payment not completed yet.'}
                    </Typography>
                  </Alert>
                )}
              </TabPanel>

              {/* Tab 5: Admit Card */}
              <TabPanel value={tabValue} index={4}>
                <Grid container spacing={3}>
                  {selectedAthlete.admitCardGenerated ? (
                    <>
                      {/* Current Admit Card */}
                      <Grid item xs={12} md={6}>
                        <Card sx={{ border: '2px solid', borderColor: 'success.main', bgcolor: 'success.lighter' }}>
                          <CardContent>
                            <Typography variant="h6" gutterBottom color="success.main" fontWeight="bold">
                              Current Admit Card
                            </Typography>
                            <Divider sx={{ my: 2 }} />
                            <Grid container spacing={2}>
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Card Number:
                                </Typography>
                                <Typography variant="h6" fontWeight="bold" color="primary">
                                  {selectedAthlete.admitCardNumber}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Generated On:
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                  {formatDate(selectedAthlete.admitCardGeneratedDate)}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Event:
                                </Typography>
                                <Typography variant="body1" fontWeight="500">
                                  {selectedAthlete.selectedEvent?.eventName}
                                </Typography>
                              </Grid>
                              <Grid item xs={12}>
                                <Typography variant="body2" color="text.secondary">
                                  Status:
                                </Typography>
                                <Chip label="Active" color="success" size="small" />
                              </Grid>
                            </Grid>

                            <Box mt={3}>
                              <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                startIcon={<Download />}
                                onClick={() => handleDownloadSingleAdmitCard(selectedAthlete)}
                              >
                                Download Admit Card
                              </Button>
                            </Box>
                          </CardContent>
                        </Card>
                      </Grid>

                      {/* Admit Card History */}
                      {selectedAthlete.admitCardHistory && selectedAthlete.admitCardHistory.length > 0 && (
                        <Grid item xs={12} md={6}>
                          <Card>
                            <CardContent>
                              <Typography variant="h6" gutterBottom fontWeight="bold">
                                Previous Admit Cards (History)
                              </Typography>
                              <Divider sx={{ my: 2 }} />
                              
                              {selectedAthlete.admitCardHistory.map((card, index) => (
                                <Accordion key={index}>
                                  <AccordionSummary expandIcon={<ExpandMore />}>
                                    <Box display="flex" alignItems="center" justifyContent="space-between" width="100%">
                                      <Typography variant="body2" fontWeight="500">
                                        Card #{card.admitCardNumber}
                                      </Typography>
                                      <Chip
                                        label={card.isActive ? 'Active' : 'Replaced'}
                                        color={card.isActive ? 'success' : 'default'}
                                        size="small"
                                        sx={{ mr: 1 }}
                                      />
                                    </Box>
                                  </AccordionSummary>
                                  <AccordionDetails>
                                    <Grid container spacing={1}>
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                          Event:
                                        </Typography>
                                        <Typography variant="body1" fontWeight="500">
                                          {card.eventName}
                                        </Typography>
                                      </Grid>
                                      <Grid item xs={12}>
                                        <Typography variant="body2" color="text.secondary">
                                          Generated:
                                        </Typography>
                                        <Typography variant="body1">
                                          {formatDate(card.generatedDate)}
                                        </Typography>
                                      </Grid>
                                      {card.replacedBy && (
                                        <Grid item xs={12}>
                                          <Typography variant="body2" color="text.secondary">
                                            Replaced By:
                                          </Typography>
                                          <Typography variant="body1" color="success.main" fontWeight="500">
                                            Card #{card.replacedBy}
                                          </Typography>
                                        </Grid>
                                      )}
                                      <Grid item xs={12}>
                                        <Alert severity="info" sx={{ mt: 2 }} icon={<Info />}>
                                          <Typography variant="caption">
                                            This card has been replaced by a newer version. For reference only.
                                          </Typography>
                                        </Alert>
                                      </Grid>
                                    </Grid>
                                  </AccordionDetails>
                                </Accordion>
                              ))}
                            </CardContent>
                          </Card>
                        </Grid>
                      )}
                    </>
                  ) : (
                    <Grid item xs={12}>
                      <Alert severity="warning" icon={<WarningIcon />}>
                        <Typography variant="body1" fontWeight="bold" mb={0.5}>
                          Admit Card Not Available
                        </Typography>
                        <Typography variant="body2">
                          {selectedAthlete.paymentStatus !== 'completed'
                            ? 'Payment needs to be completed to generate admit card.'
                            : selectedAthlete.verificationStatus !== 'approved'
                            ? 'Athlete verification is pending. Admit card will be generated after approval.'
                            : 'Admit card is being generated. Please check back soon.'}
                        </Typography>
                      </Alert>
                    </Grid>
                  )}
                </Grid>
              </TabPanel>

              {/* Tab 6: Upgrade History */}
              <TabPanel value={tabValue} index={5}>
                {selectedAthlete.upgradeHistory && selectedAthlete.upgradeHistory.length > 0 ? (
                  <TableContainer component={Paper}>
                    <Table>
                      <TableHead>
                        <TableRow sx={{ bgcolor: 'action.hover' }}>
                          <TableCell><strong>From Event</strong></TableCell>
                          <TableCell><strong>To Event</strong></TableCell>
                          <TableCell><strong>Differential Amount</strong></TableCell>
                          <TableCell><strong>Upgrade Date</strong></TableCell>
                          <TableCell><strong>Invoice</strong></TableCell>
                          <TableCell align="center"><strong>Status</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedAthlete.upgradeHistory.map((upgrade, index) => (
                          <TableRow key={index} hover>
                            <TableCell>
                              <Typography variant="body2">
                                {upgrade.fromEvent?.eventName || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="500" color="success.main">
                                {upgrade.toEvent?.eventName || 'N/A'}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" fontWeight="500">
                                ₹{upgrade.differentialAmount}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {formatDate(upgrade.upgradeDate)}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2">
                                {upgrade.upgradeInvoiceNumber || '-'}
                              </Typography>
                            </TableCell>
                            <TableCell align="center">
                              <Chip label="Completed" color="success" size="small" />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                ) : (
                  <Alert severity="info">
                    <Typography variant="body1">No upgrade history available</Typography>
                    <Typography variant="body2">
                      This athlete has not upgraded their event registration.
                    </Typography>
                  </Alert>
                )}
              </TabPanel>
            </>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 2 }}>
          {selectedAthlete && (
            <>
              {editingAthlete ? (
                <>
                  <Button onClick={handleCancelEdit}>Cancel Edit</Button>
                  <Button
                    variant="contained"
                    onClick={handleSaveEdit}
                    disabled={updatingMedicalAge}
                  >
                    {updatingMedicalAge ? <CircularProgress size={20} /> : 'Save Changes'}
                  </Button>
                </>
              ) : (
                <>
                  <Button onClick={() => setShowModal(false)}>Close</Button>
                  {selectedAthlete.invoicePath && (
                    <Button
                      onClick={() => handleDownloadSingleInvoice(selectedAthlete)}
                      variant="outlined"
                      startIcon={<Receipt />}
                      color="info"
                    >
                      Download Invoice
                    </Button>
                  )}
                  {selectedAthlete.admitCardGenerated && selectedAthlete.admitCardPath && (
                    <Button
                      onClick={() => handleDownloadSingleAdmitCard(selectedAthlete)}
                      variant="outlined"
                      startIcon={<Badge />}
                      color="secondary"
                    >
                      Download Admit Card
                    </Button>
                  )}
                  <Button
                    onClick={handleEditAthlete}
                    variant="outlined"
                    startIcon={<Edit />}
                  >
                    Edit Details
                  </Button>
                  {selectedAthlete.verificationStatus === 'pending' && (
                    <>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<ThumbDown />}
                        onClick={() =>
                          handleVerifyAthlete(selectedAthlete._id, 'rejected')
                        }
                      >
                        Reject
                      </Button>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<ThumbUp />}
                        onClick={() =>
                          handleVerifyAthlete(selectedAthlete._id, 'approved')
                        }
                      >
                        Approve
                      </Button>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </DialogActions>
      </Dialog>

      {/* Image Preview Modal */}
      <Dialog
        open={showImageModal}
        onClose={() => setShowImageModal(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {currentImage?.title}
          <IconButton
            onClick={() => setShowImageModal(false)}
            sx={{ position: 'absolute', right: 8, top: 8 }}
          >
            <Close />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {currentImage && (
            <img
              src={currentImage.url}
              alt={currentImage.title}
              style={{ width: '100%', height: 'auto' }}
            />
          )}
        </DialogContent>
        <DialogActions>
          <Button
            startIcon={<Download />}
            onClick={() =>
              handleImageDownload(currentImage?.url, currentImage?.title)
            }
          >
            Download
          </Button>
          <Button onClick={() => setShowImageModal(false)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Verification Modal */}
      <Dialog
        open={showVerificationModal}
        onClose={() => setShowVerificationModal(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Bulk Verification</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            You are about to verify {selectedAthletes.length} athlete(s).
          </Typography>
          <TextField
            fullWidth
            multiline
            rows={4}
            label="Verification Notes"
            value={verificationNotes}
            onChange={(e) => setVerificationNotes(e.target.value)}
            placeholder="Add notes for bulk verification (optional)"
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowVerificationModal(false)}>Cancel</Button>
          <Button
            onClick={() => handleBulkVerify('rejected')}
            color="error"
            variant="outlined"
            startIcon={<ThumbDown />}
          >
            Reject All
          </Button>
          <Button
            onClick={() => handleBulkVerify('approved')}
            color="success"
            variant="contained"
            startIcon={<ThumbUp />}
          >
            Approve All
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Confirmation Modal */}
      <Dialog
        open={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography variant="body1" gutterBottom>
            Are you sure you want to delete this athlete?
          </Typography>
          <Alert severity="warning" sx={{ mt: 2 }} icon={<WarningIcon />}>
            ⚠️ Warning: This action cannot be undone. If this athlete has an admit
            card number, deleting them may cause issues with the admit card numbering
            sequence.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button
            onClick={handleDeleteAthlete}
            color="error"
            variant="contained"
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={20} /> : <Delete />}
          >
            {deleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AthletesList;